import { EventBus } from '../core/EventBus';
import { logger } from '../core/Logger';
import type { CombatDamageEvent, CombatDeathEvent } from '../types/combat';
import type { Character } from './Character';
import type { DamageContext, HitReactionRule, HitReactionsConfig } from '../config/characters';
import { StateManager } from '../state/StateManager';

export class DamageReactionSystem {
  private charactersById = new Map<string, Character>();
  private cooldownRemainingSeconds = new Map<string, number>();

  constructor(private eventBus: EventBus) {
    this.eventBus.on('combat:damage', (e) => this.onDamage(e as CombatDamageEvent));
    this.eventBus.on('combat:death', (e) => this.onDeath(e as CombatDeathEvent));
  }

  setCharacters(characters: Character[]): void {
    this.charactersById.clear();
    for (const c of characters) this.charactersById.set(c.getId(), c);
  }

  update(deltaTime: number): void {
    if (deltaTime <= 0) return;
    for (const [id, remaining] of this.cooldownRemainingSeconds.entries()) {
      const next = remaining - deltaTime;
      if (next <= 0) this.cooldownRemainingSeconds.delete(id);
      else this.cooldownRemainingSeconds.set(id, next);
    }
  }

  private onDamage(event: CombatDamageEvent): void {
    const target = this.charactersById.get(event.targetId);
    if (!target) return;
    if (target.hasLifecycleScripts()) return;

    if ((this.cooldownRemainingSeconds.get(event.targetId) ?? 0) > 0) return;

    const anim = target.animationController;
    if (!anim) return;

    const hitReactionCallback = target.getHitReactionCallback();
    if (hitReactionCallback) {
      const context: DamageContext = {
        baseDamage: event.damage,
        attackerId: event.attackerId,
        targetId: event.targetId,
        attackId: event.attackDefId,
        source: event.source,
        impactDirection: event.impactDirection ? event.impactDirection.clone() : undefined,
        impactNormal: event.impactNormal ? event.impactNormal.clone() : undefined
      };
      try {
        const overrideAnimation = hitReactionCallback(context);
        if (overrideAnimation) {
          anim.playOneShot(overrideAnimation, () => {
            // Send SYNC_LOCOMOTION event to force state machine to re-trigger entry animation
            StateManager.getInstance().sendCharacterEvent(event.targetId, { type: 'SYNC_LOCOMOTION' });
          });
          const cooldown = this.resolveCooldownSeconds(target);
          if (cooldown > 0) {
            this.cooldownRemainingSeconds.set(event.targetId, cooldown);
          }
          return;
        }
      } catch (err) {
        logger.animation.error('DamageReactionSystem: onHitReaction callback failed', {
          targetId: event.targetId,
          err
        });
      }
    }

    const cfg = (target as any).characterConfig?.hitReactions as HitReactionsConfig | undefined;
    if (!cfg || !Array.isArray(cfg.reactions) || cfg.reactions.length === 0) return;

    const candidates = cfg.reactions.filter((r) => this.matchesRule(r, event));
    const chosen = this.chooseWeighted(candidates);
    if (!chosen) return;

    try {
      anim.playOneShot(chosen.animation, () => {
        // Send SYNC_LOCOMOTION event to force state machine to re-trigger entry animation
        StateManager.getInstance().sendCharacterEvent(event.targetId, { type: 'SYNC_LOCOMOTION' });
      });
    } catch (err) {
      logger.animation.warn('DamageReactionSystem: failed to play hit reaction', {
        targetId: event.targetId,
        animation: chosen.animation,
        err,
      });
      return;
    }

    const cd = this.resolveCooldownSeconds(target, cfg);
    if (cd > 0) this.cooldownRemainingSeconds.set(event.targetId, cd);
  }

  private onDeath(event: CombatDeathEvent): void {
    const target = this.charactersById.get(event.characterId);
    if (!target) return;
    if (target.hasLifecycleScripts()) return;

    const cfg = (target as any).characterConfig?.hitReactions as HitReactionsConfig | undefined;
    const deathAnim = cfg?.death?.animation;
    if (!deathAnim) return;

    const anim = target.animationController;
    if (!anim) return;

    try {
      anim.playOneShot(deathAnim);
    } catch {
      // playOneShot already logs missing animations; keep death handler silent.
    }
  }

  private resolveCooldownSeconds(target: Character, cfg?: HitReactionsConfig): number {
    if (cfg && typeof cfg.cooldownSeconds === 'number') {
      return cfg.cooldownSeconds;
    }
    const fallback = (target as any).characterConfig?.hitReactions as HitReactionsConfig | undefined;
    if (fallback && typeof fallback.cooldownSeconds === 'number') {
      return fallback.cooldownSeconds;
    }
    return 0.25;
  }

  private matchesRule(rule: HitReactionRule, event: CombatDamageEvent): boolean {
    const minDamage = rule.minDamage ?? -Infinity;
    const maxDamage = rule.maxDamage ?? Infinity;
    if (event.damage < minDamage) return false;
    if (event.damage > maxDamage) return false;

    const src = rule.source ?? 'any';
    if (src !== 'any') {
      // If event.source is missing, treat as non-match for specific filters.
      if (!event.source) return false;
      if (event.source !== src) return false;
    }

    const chance = rule.chance ?? 1;
    if (chance <= 0) return false;
    if (chance < 1 && Math.random() > chance) return false;

    return true;
  }

  private chooseWeighted(rules: HitReactionRule[]): HitReactionRule | null {
    if (rules.length === 0) return null;
    if (rules.length === 1) return rules[0];

    let total = 0;
    for (const r of rules) total += Math.max(0, r.weight ?? 1);
    if (total <= 0) return rules[0];

    let roll = Math.random() * total;
    for (const r of rules) {
      roll -= Math.max(0, r.weight ?? 1);
      if (roll <= 0) return r;
    }

    return rules[rules.length - 1];
  }
}
